// WebSocket chat gateway contract — mirrors backend api/ws_frames.py.
// Single source of truth for the wire protocol. Frames are flat JSON keyed by `type`.

// Endpoint path appended to the WS origin (see BASE_URL).
export const CHAT_WS_PATH = "/ws/chat";

// Subprotocols offered on the handshake. The gateway closes the socket unless
// exactly one channel subprotocol and the chat subprotocol are present.
export const CHAT_SUBPROTOCOL = "cc.chat.v1";
export const CHANNEL_SUBPROTOCOL_PREFIX = "cc.channel.";
export const RESUME_SUBPROTOCOL_PREFIX = "cc.resume.";
export const RSECRET_SUBPROTOCOL_PREFIX = "cc.rsecret.";
export const IDLE_TIMEOUT_CLOSE_CODE = 4408;

export const enum FrameType {
  // Client -> Server
  USER_MESSAGE = "user_message",

  // Server -> Client
  READY = "ready",
  HISTORY = "history",
  ASSISTANT_MESSAGE = "assistant_message",
  OPERATOR_MESSAGE = "operator_message",
  HANDOFF_STATUS = "handoff_status",
  ERROR = "error",
  CLOSED = "closed",
}

export enum ProductType {
  PART = "part",
  PRODUCT = "product",
  MODEL = "model",
}

// Operator-handoff lifecycle states, pushed via `handoff_status` frames.
export const enum HandoffState {
  OFFERED = "offered",
  WAITING = "waiting",
  ATTENDED = "attended",
  ENDED = "ended",
  CAPTURED = "captured",
  UNAVAILABLE = "unavailable",
}

export type TLanguage = "en" | "es" | "fr";

// Client -> Server
export type TUserMessageFrame = {
  type: FrameType.USER_MESSAGE;
  text: string;
  language?: TLanguage;
  debug: boolean;
};

export type TCard = {
  type: ProductType;
  sku: string;
  name?: string;
  product_title?: string;
  discontinued?: boolean | null;
  face?: string | null;
  url?: string | null;
  mfg?: string | null;
  oem_part_number?: string | null;
  description?: string | null;
  image_url?: string | null;
  price?: {
    amount: string;
    currency: string;
  } | null;
};

export type TComparisonItem = {
  id: string;
  title: string;
  url?: string | null;
};

export type TComparisonValue = string | boolean | {
  amount: string;
  currency: string;
} | null;

export type TComparisonRow = {
  key: string;
  name?: string | null;
  values: Record<string, TComparisonValue>;
};

export type TComparisonTable = {
  items: TComparisonItem[];
  rows: TComparisonRow[];
};

export type TSuggestedAction = {
  type: "clarify" | "escalate" | "view_product";
  label: string;
  url?: string | null;
  sku?: string | null;
};

export type TFrameMetadata = {
  retrieved_count: number;
  safety_ok: boolean;
  degraded_reason?: string | null;
  latency_ms?: number;
  escalate?: boolean;
  cards_suppressed?: boolean;
};

export type TReadyFrame = {
  type: FrameType.READY;
  protocol_version: "1";
  conversation_id: string;
  resume_secret?: string;
};

export type THistoryMessageItem = {
  role: "user" | "assistant" | "operator";
  text: string;
  created_at: string;
};

export type THistoryFrame = {
  type: FrameType.HISTORY;
  conversation_id: string;
  messages: THistoryMessageItem[];
  truncated: boolean;
};

export type TAssistantMessageFrame = {
  type: FrameType.ASSISTANT_MESSAGE;
  conversation_id: string;
  message_id?: string;
  response: string;
  cards: TCard[];
  comparison_data?: TComparisonTable | null;
  suggested_actions: TSuggestedAction[];
  intent: string;
  language: string;
  metadata: TFrameMetadata;
  // Present only when the request was sent with `debug: true` — raw
  // backend diagnostics, shape not contractual. Surfaced as-is in the debugger.
  debug?: Record<string, unknown> | null;
};

export type TOperatorMessageFrame = {
  type: FrameType.OPERATOR_MESSAGE;
  conversation_id: string;
  text: string;
  label: string;
  sent_at: string;
  seq: number;
};

export type THandoffStatusFrame = {
  type: FrameType.HANDOFF_STATUS;
  conversation_id: string;
  state: HandoffState;
  reason: string | null;
};

export type TErrorFrame = {
  type: FrameType.ERROR;
  code: string;
  reason: string;
};

export type TClosedFrame = {
  type: FrameType.CLOSED;
  reason: string;
};

// Any frame the server can push to the client.
export type TServerFrame =
  | TReadyFrame
  | THistoryFrame
  | TAssistantMessageFrame
  | TOperatorMessageFrame
  | THandoffStatusFrame
  | TErrorFrame
  | TClosedFrame;
